import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import {
  FeatureTableOptions,
  GeckoEditableFeature,
} from 'types/assetModel/features/baseClasses/geckoEditableFeature'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentCoords } from 'types/geometric/geckoComponentCoords'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { requiredNumberSchema, requiredStringSchema } from 'utils/zodHelpers'

export interface GeckoFixedSensorJSON {
  type: GeckoFeatureModelType.FixedSensor
  name: string
  feature_geometry: {
    x: number
    y: number
  }
}

export const NewGeckoFixedSensorJSON: GeckoFixedSensorJSON = {
  type: GeckoFeatureModelType.FixedSensor,
  name: 'New Sensor',
  feature_geometry: {
    x: 0,
    y: 0,
  },
}

/**
 * A class representing a Barnacle placed onto a component and whose data is
 * available in the FDE Barnacles Extension
 * More details can be found here:
 * https://geckorobotics.atlassian.net/wiki/spaces/FDE/pages/3995762779/Barnacles
 */
export class GeckoFixedSensor extends GeckoEditableFeature {
  readonly name: string
  readonly type = GeckoFeatureModelType.FixedSensor
  readonly geometry: GeckoComponentCoords

  private constructor(
    id: string,
    name: string,
    geometry: GeckoComponentCoords
  ) {
    super(id)
    this.name = name
    this.geometry = geometry
  }

  public static CreateFromJSON(
    componentID: string,
    sensorID: string,
    sensorJSON: GeckoFixedSensorJSON
  ): GeckoFixedSensor {
    const featureGeometryData = sensorJSON.feature_geometry as Record<
      string,
      number
    >
    const sensorGeometry = GeckoComponentCoords.CreateFromValues(
      componentID,
      featureGeometryData.x,
      GeckoMeasurementUnit.Meters,
      featureGeometryData.y,
      GeckoMeasurementUnit.Meters
    )

    const sensorName = sensorJSON.name as string

    return new GeckoFixedSensor(sensorID, sensorName, sensorGeometry)
  }

  public static CreateMapFromJSON(
    componentID: string,
    recordsJSON: Record<string, GeckoFixedSensorJSON>
  ): Map<string, GeckoFixedSensor> {
    return Object.keys(recordsJSON).reduce((resultObj, manwayID) => {
      resultObj.set(
        manwayID,
        GeckoFixedSensor.CreateFromJSON(
          componentID,
          manwayID,
          recordsJSON[manwayID]
        )
      )
      return resultObj
    }, new Map<string, GeckoFixedSensor>())
  }

  asJSON() {
    return generateSensor(this.name, this.geometry.x, this.geometry.y)
  }
}

export function generateSensor(
  name: string,
  x: GeckoMeasurement,
  y: GeckoMeasurement
): GeckoFixedSensorJSON {
  return {
    type: GeckoFeatureModelType.FixedSensor,
    name: name,
    feature_geometry: {
      x: x.toNumber(GeckoMeasurementUnit.Meters),
      y: y.toNumber(GeckoMeasurementUnit.Meters),
    },
  }
}

export const FixedSensorFormFields = (sensorJSON: GeckoFixedSensorJSON) => {
  return [
    {
      dataKey: 'name',
      label: 'Sensor Name',
      value: sensorJSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'x',
      label: 'X Position',
      value: sensorJSON.feature_geometry.x,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'y',
      label: 'Y Position',
      value: sensorJSON.feature_geometry.y,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
  ]
}

export const FixedSensorJSONFromFormFields = (formFields: FormField[]) => {
  return generateSensor(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'x')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'y')?.value as number,
      GeckoMeasurementUnit.Meters
    )
  )
}

export const FixedSensorFeatureTableOptions: FeatureTableOptions = {
  type: GeckoFeatureModelType.FixedSensor,
  title: 'Fixed Sensors',
  singularName: 'Fixed Sensor',
  fields: ['x', 'y'],
}
